/**
 * Red banner shown when SES is in sandbox-mode (OCP-94).
 *
 * Renders on AdminSettingsPage above the settings list. The banner
 * polls `GET /v1/admin/ses/quota` once on mount; the operator can
 * trigger a re-check via the "Re-check now" button which calls
 * `POST /v1/admin/ses/check-now`.
 *
 * Three rendered states:
 *  - `sandbox=true`  → red banner pointing at the production-access
 *    runbook with the current `Max24HourSend` / `MaxSendRate` numbers
 *  - `sandbox=false` → green compact "production access verified"
 *    confirmation, fades after 5 seconds via local state
 *  - `result=null` or `ok=false` → neutral / amber "couldn't determine"
 *    fallback
 *
 * Brand red (Tailwind `red-*`) is used instead of `brand-*` so the
 * banner doesn't blend with the rest of the admin UI; the WARN is
 * meant to be visually distinct.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';

interface QuotaSnapshot {
  ok: true;
  mode: 'live' | 'mock';
  quota: { max24HourSend: number; sentLast24Hours: number; maxSendRate: number };
  sandbox: boolean;
  checkedAt: string;
}

interface QuotaFailure {
  ok: false;
  mode: 'live' | 'mock';
  error: string;
  checkedAt: string;
}

type QuotaResult = QuotaSnapshot | QuotaFailure;

interface QuotaResponse {
  result: QuotaResult | null;
  mockMode: boolean;
}

export function SesQuotaBanner() {
  const [state, setState] = useState<QuotaResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api<QuotaResponse>('/v1/admin/ses/quota');
      setState(r);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Could not load SES quota (HTTP ${err.status}).`);
      } else {
        setError('Could not load SES quota.');
      }
    }
  }, []);

  const recheck = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api<QuotaResponse>('/v1/admin/ses/check-now', { method: 'POST' });
      setState(r);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Re-check failed (HTTP ${err.status}).`);
      } else {
        setError('Re-check failed.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!state) {
    if (error) {
      return (
        <div
          role="alert"
          className="mb-6 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-200"
        >
          {error}
        </div>
      );
    }
    return null;
  }

  const result = state.result;

  // Boot-check hasn't completed yet
  if (result === null) {
    return (
      <div className="mb-6 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
        SES quota check still running…
      </div>
    );
  }

  // Check itself failed (auth, network, etc.)
  if (!result.ok) {
    return (
      <div
        role="alert"
        data-testid="ses-banner-error"
        className="mb-6 rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/30 px-4 py-3 text-sm text-amber-900 dark:text-amber-200"
      >
        <div className="flex items-baseline justify-between">
          <div>
            <strong className="font-semibold">SES quota check failed</strong> — could not
            determine sandbox status. Treat as sandbox until next successful check.
            <div className="mt-1 font-mono text-xs">{result.error}</div>
          </div>
          <button
            type="button"
            onClick={() => void recheck()}
            disabled={loading}
            className="ml-3 shrink-0 rounded border border-amber-300 bg-white dark:bg-gray-900 px-2 py-0.5 text-xs hover:bg-amber-100 disabled:opacity-50"
          >
            {loading ? 'Re-checking…' : 'Re-check now'}
          </button>
        </div>
      </div>
    );
  }

  if (result.sandbox) {
    return (
      <div
        role="alert"
        data-testid="ses-banner-sandbox"
        className="mb-6 rounded border-2 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-900 dark:text-red-200"
      >
        <div className="flex items-baseline justify-between gap-3">
          <div className="space-y-1">
            <div className="text-base font-semibold">
              SES is in SANDBOX mode — viral launch will fail
            </div>
            <p>
              AWS SES caps this account at{' '}
              <span className="font-mono font-semibold">
                {result.quota.max24HourSend.toLocaleString()}
              </span>{' '}
              emails / 24h at{' '}
              <span className="font-mono font-semibold">{result.quota.maxSendRate}</span> mail/sec.
              The waitlist Double-Opt-In flow burns this quota at ~50 signups/day.
            </p>
            <p>
              File a production-access-request via AWS Support (24-48h lead-time). The full
              application text is in{' '}
              <code className="rounded bg-red-100 px-1 font-mono text-xs">
                runbooks/ses-production-access.md
              </code>
              .
            </p>
            {result.mode === 'mock' && (
              <p className="text-xs text-red-700 dark:text-red-300">
                (Currently in MOCK mode — set <code className="font-mono">SES_ACCESS_KEY_ID</code>{' '}
                + <code className="font-mono">SES_SECRET_ACCESS_KEY</code> for live readings.)
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => void recheck()}
            disabled={loading}
            className="shrink-0 rounded border border-red-300 dark:border-red-700 bg-white dark:bg-gray-900 px-3 py-1 text-xs font-medium hover:bg-red-100 disabled:opacity-50"
          >
            {loading ? 'Re-checking…' : 'Re-check now'}
          </button>
        </div>
      </div>
    );
  }

  // Production-mode confirmation
  return (
    <div
      data-testid="ses-banner-ok"
      className="mb-6 rounded border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 px-4 py-2 text-sm text-emerald-900"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <strong className="font-semibold">SES production access verified</strong> —{' '}
          <span className="font-mono">
            {result.quota.max24HourSend.toLocaleString()} mail/24h @ {result.quota.maxSendRate}
            /sec
          </span>
          {result.mode === 'mock' && (
            <span className="ml-2 text-xs text-emerald-700 dark:text-emerald-300">(mock mode)</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => void recheck()}
          disabled={loading}
          className="shrink-0 rounded border border-emerald-300 bg-white dark:bg-gray-900 px-2 py-0.5 text-xs hover:bg-emerald-100 disabled:opacity-50"
        >
          {loading ? 'Re-checking…' : 'Re-check'}
        </button>
      </div>
    </div>
  );
}
